import React from "react";
import { Link } from "react-router-dom";

import cx from "../ui/cx";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { resetListPages } from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import {
  AppModuleActionProps,
  AppModuleProps,
  dailyUsageModules,
  getVisibleModules,
  visibleActions,
} from "../../static/modules";

const tileButtonClass =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 border h-9 rounded-md px-3 w-full text-xs md:text-sm lg:text-base py-1 md:py-2";

// The first button is the main one, filled; the others are outlined.
const filledButtonClass = cx(
  tileButtonClass,
  "border-[#113F67] bg-[#113F67] text-white hover:border-[#0d2f4f] hover:bg-[#0d2f4f]"
);

const outlineButtonClass = cx(
  tileButtonClass,
  "border-[#34699A] bg-transparent text-[#34699A] hover:bg-[#34699A] hover:text-white"
);

// A tile's buttons: its actions the user may open, or one button to the module's page.
const tileActions = (
  dailyUsageModule: AppModuleProps,
  permissions: string[] | undefined
): AppModuleActionProps[] =>
  dailyUsageModule.actions
    ? visibleActions(dailyUsageModule, permissions)
    : [
        {
          label: "ખોલો",
          href: dailyUsageModule.href,
          listKey: dailyUsageModule.listKey,
          permission: dailyUsageModule.permission,
        },
      ];

const DailyUsageModules = () => {
  const dispatch = useAppDispatch();
  const loggingUser = useAppSelector(getLoggingUser);

  const visibleModules = getVisibleModules(
    dailyUsageModules,
    loggingUser?.permissions
  );

  if (visibleModules.length === 0) return null;

  return (
    <div className="mb-6 md:mb-8">
      <h2 className="text-base md:text-lg font-semibold text-[#113F67] mb-3 md:mb-4">
        દૈનિક ઉપયોગ મોડ્યુલ
      </h2>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {visibleModules.map((dailyUsageModule) => {
          const actions = tileActions(
            dailyUsageModule,
            loggingUser?.permissions
          );

          // The card isn't a link: each button opens its own page. It grows past its height when text wraps.
          return (
            <div
              key={dailyUsageModule.title}
              className="rounded-lg bg-card text-card-foreground shadow-sm transition-all duration-200 hover:shadow-lg border-2 hover:border-[#34699A] flex flex-col min-h-44 sm:min-h-48 md:min-h-56 lg:min-h-64"
            >
              <div className="flex flex-col space-y-1.5 text-center px-2 md:px-6 pt-3 md:pt-6 pb-4">
                <div className="text-xl md:text-3xl lg:text-4xl mb-1 md:mb-2">
                  {dailyUsageModule.icon}
                </div>
                {/* text-lg/xl bring their own line height, so leading-tight is repeated to win over them. */}
                <div className="tracking-tight text-sm md:text-lg lg:text-xl font-bold text-[#113F67] leading-tight md:leading-tight lg:leading-tight">
                  {dailyUsageModule.title}
                </div>
                <div className="text-xs md:text-sm text-gray-600 line-clamp-2">
                  {dailyUsageModule.description}
                </div>
              </div>

              <div
                className={cx(
                  "mt-auto grid gap-3 px-2 md:px-6 pb-3 md:pb-6",
                  actions.length > 1 ? "grid-cols-2" : "grid-cols-1"
                )}
              >
                {actions.map((action, index) => (
                  <Link
                    key={action.href}
                    to={action.href}
                    onClick={() => dispatch(resetListPages(action.listKey))}
                    className={
                      index === 0 ? filledButtonClass : outlineButtonClass
                    }
                  >
                    {action.label}
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default DailyUsageModules;
